import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import Administration from "../../session/Administration";
import LocalAuthDialogFields from "../field/LocalAuthDialogFields";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import {InputSwitch} from "primereact/inputswitch";
import TimeUnit from "../../../shared/units/TimeUnit";
import {InputNumber} from "primereact/inputnumber";
import FileSizeUnit from "../../../shared/units/FileSizeUnit";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {TabPanel, TabView} from "primereact/tabview";
import Info, {InfoType} from "../../../shared/components/Info";
import {
	ApplicationInterface,
	ConfigurationType,
	LocalAuthRest,
	LocalAuthSoap, PortalAuthTimeout,
	Server,
	ServerConfigLocalAuth,
	SessionAccessToken,
	SessionLimits,
	SessionRefreshToken
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import MessageBox from "../../../shared/components/MessageBox";
import Immutable from "../../../shared/immutable/Immutable";

interface LocalAuthDialogStatus extends AdminDialogState<ServerConfigLocalAuth> {
	panel: number;
}

interface LocalAuthDialogProperties extends AdminDialogProperties<ServerConfigLocalAuth> {
	applicationConfig?: Immutable<ApplicationInterface>;
}

export class LocalAuthDialogDefinition extends AdminDialog<ServerConfigLocalAuth, LocalAuthDialogProperties,
	LocalAuthDialogStatus> {
	constructor(props: Readonly<LocalAuthDialogProperties>) {
		super(props);
		this.state = {
			...this.state,
			panel: 0
		};
		this.onChange = this.onChange.bind(this);
	}

	public renderRestGeneralTab(parameter: ServerConfigLocalAuth): ReactNode {
		let accessTokenExpiration: number = FunctionHelper.applyValue(
			parameter.rest.accessToken?.expiration, SessionAccessToken.getExpirationDefault()
		);
		let applicationConfig: ApplicationInterface | undefined = this.props.applicationConfig?.get();
		let autoRefresh: number = FunctionHelper.applyValue(
			applicationConfig?.portal.auth?.timeout?.autoRefresh, PortalAuthTimeout.getAutoRefreshDefault()
		);

		return (
			<>
				<div className="p-col-12 p-grid">
					<label className="p-col-12 p-sm-5" htmlFor="control-rest-enabled">
						<Translate {...this.props} data="local_auth.rest.enabled" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={LocalAuthRest.getEnabledDescription()}/>
					</label>
					<div className="p-col-12 p-sm-7 switch-container">
						<InputSwitch inputId="control-rest-enabled"
									 checked={FunctionHelper.applyValue(
										 parameter.rest.enabled, LocalAuthRest.getEnabledDefault()
									 )}
									 onChange={(e) => this.onChange(LocalAuthDialogFields.REST_ENABLED, e.value)}/>
					</div>
				</div>
				<div className="p-col-12 p-grid"
					 hidden={!FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())}>
					<label className="p-col-12 p-sm-5" htmlFor="control-rest-anonymous">
						<Translate {...this.props} data="local_auth.rest.anonymous" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={LocalAuthRest.getAllowAnonymousDescription()}/>
					</label>
					<div className="p-col-12 p-sm-7 switch-container">
						<InputSwitch inputId="control-soap-anonymous"
									 checked={FunctionHelper.applyValue(
										 parameter.rest.allowAnonymous, LocalAuthRest.getAllowAnonymousDefault()
									 )}
									 onChange={(e) => this.onChange(
										 LocalAuthDialogFields.REST_ANONYMOUS, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid"
					 hidden={!FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())}>
					<label className="p-col-12 p-sm-5" htmlFor="control-rest-max-skew">
						<Translate {...this.props} data="local_auth.rest.max_skew" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={LocalAuthRest.getMaxTimeSkewDescription()}/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-rest-max-skew" min={0} step={1} showButtons={true}
									 suffix={" " + TimeUnit.SECOND} useGrouping={false}
									 value={FunctionHelper.applyValue(
										 parameter.rest?.maxTimeSkew, LocalAuthRest.getMaxTimeSkewDefault()
									 )}
									 onChange={(e) => this.onChange(
										 LocalAuthDialogFields.TIME_SKEW, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid"
					 hidden={!FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())}>
					<label className="p-col-12 p-sm-5" htmlFor="control-rest-refresh-expiration">
						<Translate {...this.props} data="local_auth.rest.refresh_expiration" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={SessionRefreshToken.getExpirationDescription()}/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-rest-refresh-expiration" suffix={" " + TimeUnit.SECOND}
									 showButtons={true} step={1} useGrouping={false}
									 value={FunctionHelper.applyValue(
										 parameter.rest?.refreshToken?.expiration,
										 SessionRefreshToken.getExpirationDefault()
									 )}
									 min={SessionRefreshToken.getExpirationMin()}
									 onChange={(e) => this.onChange(
										 LocalAuthDialogFields.REFRESH_EXPIRATION, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid"
					 hidden={
						 !FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())
					 }>
					<label className="p-col-12 p-sm-5" htmlFor="control-rest-access-expiration">
						<Translate {...this.props} data="local_auth.rest.access_expiration" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={SessionAccessToken.getExpirationDescription()}/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-rest-access-expiration" suffix={" " + TimeUnit.SECOND}
									 step={1} showButtons={true} useGrouping={false}
									 value={FunctionHelper.applyValue(
										 parameter.rest?.accessToken?.expiration,
										 SessionAccessToken.getExpirationDefault()
									 )}
									 max={FunctionHelper.applyValue(
										 parameter.rest?.refreshToken?.expiration,
										 SessionRefreshToken.getExpirationDefault()
									 )}
									 min={SessionAccessToken.getExpirationMin()}
									 onChange={(e) => this.onChange(
										 LocalAuthDialogFields.ACCESS_EXPIRATION, e.value
									 )}/>
					</div>
				</div>
				<MessageBox type={MessageType.WARNING} language={this.props.language}
								visible={autoRefresh >= accessTokenExpiration}>
					<Translate {...this.props} data="portal_auth.auto_refresh.limit" namespace="dialog"
							   values={{expiry: autoRefresh + " " + TimeUnit.SECOND}}/>
				</MessageBox>
			</>
		);
	}

	public renderRestLimitTab(parameter: ServerConfigLocalAuth): ReactNode {
		return (
			<>
				<legend style={{marginLeft: "15px"}}
						hidden={!FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())}>
					<Translate {...this.props} data="local_auth.rest.diskspace.legend" namespace="dialog"/>
				</legend>
				<div className="p-col-12 p-grid"
					 hidden={!FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())}>
					<label className="p-col-12 p-sm-5" htmlFor="control-rest-diskspace-anonymous">
						<Translate {...this.props} data="local_auth.rest.diskspace.anonymous" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={SessionLimits.getDiskSpaceLimitDescription()}/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-rest-diskspace-anonymous" step={1} showButtons={true}
									 suffix={" " + FileSizeUnit.MEGABYTES} useGrouping={false}
									 value={FunctionHelper.applyValue(
										 parameter.rest.limits?.diskSpaceLimit, SessionLimits.getDiskSpaceLimitDefault()
									 )}
									 min={SessionLimits.getDiskSpaceLimitMin()}
									 onChange={(e) => this.onChange(
										 LocalAuthDialogFields.DISKSPACE_ANONYMOUS, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid"
					 hidden={!FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())}>
					<label className="p-col-12 p-sm-5" htmlFor="control-rest-diskspace-user">
						<Translate {...this.props} data="local_auth.rest.diskspace.user" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={SessionLimits.getDiskSpaceLimitUserDescription()}/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-rest-diskspace-user" step={1} showButtons={true}
									 suffix={" " + FileSizeUnit.MEGABYTES} useGrouping={false}
									 value={FunctionHelper.applyValue(
										 parameter.rest.limits?.diskSpaceLimitUser,
										 SessionLimits.getDiskSpaceLimitUserDefault()
									 )}
									 min={SessionLimits.getDiskSpaceLimitUserMin()}
									 onChange={(e) => this.onChange(
										 LocalAuthDialogFields.DISKSPACE_USER, e.value
									 )}/>
					</div>
				</div>
				<legend style={{marginLeft: "15px"}}
						hidden={!FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())}>
					<Translate {...this.props} data="local_auth.rest.document.legend" namespace="dialog"/>
				</legend>
				<div className="p-col-12 p-grid"
					 hidden={!FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())}>
					<label className="p-col-12 p-sm-5" htmlFor="control-rest-document-anonymous">
						<Translate {...this.props} data="local_auth.rest.document.anonymous" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={SessionLimits.getMaxFilesDescription()}/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-rest-document-anonymous" step={1} showButtons={true}
									 useGrouping={false}
									 value={FunctionHelper.applyValue(
										 parameter.rest.limits?.maxFiles, SessionLimits.getMaxFilesDefault()
									 )}
									 min={SessionLimits.getMaxFilesMin()}
									 onChange={(e) => this.onChange(
										 LocalAuthDialogFields.MAXFILES_ANONYMOUS, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid"
					 hidden={!FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())}>
					<label className="p-col-12 p-sm-5" htmlFor="control-rest-document-user">
						<Translate {...this.props} data="local_auth.rest.document.user" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={SessionLimits.getMaxFilesUserDescription()}/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-rest-document-user" step={1} showButtons={true}
									 useGrouping={false}
									 value={FunctionHelper.applyValue(
										 parameter.rest.limits?.maxFilesUser, SessionLimits.getMaxFilesUserDefault()
									 )}
									 min={SessionLimits.getMaxFilesUserMin()}
									 onChange={(e) => this.onChange(
										 LocalAuthDialogFields.MAXFILES_USER, e.value
									 )}/>
					</div>
				</div>
				<legend style={{marginLeft: "15px"}}
						hidden={!FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())}>
					<Translate {...this.props} data="local_auth.rest.upload.legend" namespace="dialog"/>
				</legend>
				<div className="p-col-12 p-grid"
					 hidden={!FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())}>
					<label className="p-col-12 p-sm-5" htmlFor="control-rest-upload-anonymous">
						<Translate {...this.props} data="local_auth.rest.upload.anonymous" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={SessionLimits.getUploadLimitDescription()}/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-rest-upload-anonymous" step={1} showButtons={true}
									 suffix={" " + FileSizeUnit.MEGABYTES} useGrouping={false}
									 value={FunctionHelper.applyValue(
										 parameter.rest.limits?.uploadLimit, SessionLimits.getUploadLimitDefault()
									 )}
									 min={SessionLimits.getUploadLimitMin()}
									 onChange={(e) => this.onChange(
										 LocalAuthDialogFields.UPLOAD_ANONYMOUS, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid"
					 hidden={!FunctionHelper.applyValue(parameter.rest.enabled, LocalAuthRest.getEnabledDefault())}>
					<label className="p-col-12 p-sm-5" htmlFor="control-rest-upload-user">
						<Translate {...this.props} data="local_auth.rest.upload.user" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={SessionLimits.getUploadLimitUserDescription()}/>
					</label>
					<div className="p-col-12 p-sm-7">
						<InputNumber inputId="control-rest-upload-user" step={1} showButtons={true}
									 suffix={" " + FileSizeUnit.MEGABYTES} useGrouping={false}
									 value={FunctionHelper.applyValue(
										 parameter.rest.limits?.uploadLimitUser,
										 SessionLimits.getUploadLimitUserDefault()
									 )}
									 min={SessionLimits.getUploadLimitUserMin()}
									 onChange={(e) => this.onChange(
										 LocalAuthDialogFields.UPLOAD_USER, e.value
									 )}/>
					</div>
				</div>
			</>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ServerConfigLocalAuth | undefined = this.state.parameter;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-enabled">
							<Translate {...this.props} data="local_auth.enabled" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={ServerConfigLocalAuth.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-enabled"
										 checked={FunctionHelper.applyValue(
											 parameter?.enabled, ServerConfigLocalAuth.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 LocalAuthDialogFields.ENABLED, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
				<fieldset className="p-col-12 p-grid">
					<legend>
						<Translate {...this.props} data="local_auth.soap.label" namespace="dialog"/>
					</legend>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-soap-enabled">
							<Translate {...this.props} data="local_auth.soap.enabled" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={LocalAuthSoap.getEnabledDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-soap-enabled"
										 checked={FunctionHelper.applyValue(
											 parameter?.soap.enabled, LocalAuthSoap.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 LocalAuthDialogFields.SOAP_ENABLED, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!FunctionHelper.applyValue(parameter?.soap.enabled, LocalAuthSoap.getEnabledDefault())}>
						<label className="p-col-12 p-sm-5" htmlFor="control-soap-anonymous">
							<Translate {...this.props} data="local_auth.soap.anonymous" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={LocalAuthSoap.getAllowAnonymousDescription()}/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-soap-anonymous"
										 checked={FunctionHelper.applyValue(
											 parameter?.soap.allowAnonymous, LocalAuthSoap.getAllowAnonymousDefault()
										 )}
										 onChange={(e) => this.onChange(
											 LocalAuthDialogFields.SOAP_ANONYMOUS, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
				<fieldset className="p-col-12 p-grid">
					<legend>
						<Translate {...this.props} data="local_auth.rest.label" namespace="dialog"/>
					</legend>
					{!FunctionHelper.applyValue(parameter?.rest.enabled, LocalAuthRest.getEnabledDefault()) &&
						<div className="p-col-12 p-grid">
							<label className="p-col-12 p-sm-5" htmlFor="control-rest-enabled">
								<Translate {...this.props} data="local_auth.rest.enabled" namespace="dialog"/>
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={LocalAuthRest.getEnabledDescription()}/>
							</label>
							<div className="p-col-12 p-sm-7 switch-container">
								<InputSwitch inputId="control-rest-enabled"
											 checked={FunctionHelper.applyValue(
												 parameter?.rest.enabled, LocalAuthRest.getEnabledDefault()
											 )}
											 onChange={(e) => this.onChange(
												 LocalAuthDialogFields.REST_ENABLED, e.value
											 )}/>
							</div>
						</div>
					}
					{FunctionHelper.applyValue(parameter?.rest.enabled, LocalAuthRest.getEnabledDefault()) &&
						<TabView activeIndex={this.state.panel} onTabChange={
							(e) => this.setState({panel: e.index})
						}>
							<TabPanel header={Translate.value("local_auth.rest.general", {ns: "dialog"})}>
								{this.renderRestGeneralTab(parameter!)}
							</TabPanel>
							<TabPanel header={Translate.value("local_auth.rest.limit", {ns: "dialog"})}>
								{this.renderRestLimitTab(parameter!)}
							</TabPanel>
						</TabView>
					}
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["local-auth-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.local_auth.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-shield"}/>
					<Translate {...this.props} data="local_auth.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: LocalAuthDialogFields, value: any): void {
		let parameter: ServerConfigLocalAuth | undefined = this.state.parameter as ServerConfigLocalAuth;

		switch (field) {
			case LocalAuthDialogFields.ENABLED:
				parameter.enabled = value;
				break;
			case LocalAuthDialogFields.SOAP_ENABLED:
				parameter.soap.enabled = value;
				break;
			case LocalAuthDialogFields.SOAP_ANONYMOUS:
				parameter.soap.allowAnonymous = value;
				break;
			case LocalAuthDialogFields.REST_ENABLED:
				parameter.rest.enabled = value;
				break;
			case LocalAuthDialogFields.REST_ANONYMOUS:
				parameter.rest.allowAnonymous = value;
				break;
			case LocalAuthDialogFields.TIME_SKEW:
				parameter.rest.maxTimeSkew = value;
				break;
			case LocalAuthDialogFields.REFRESH_EXPIRATION:
				if (!FunctionHelper.exists(parameter.rest.refreshToken)) {
					parameter.rest.refreshToken = new SessionRefreshToken({});
				}

				parameter.rest.refreshToken!.expiration = value;

				if (FunctionHelper.applyValue(
					parameter.rest.accessToken?.expiration, SessionAccessToken.getExpirationDefault()
				) > value) {
					if (!FunctionHelper.exists(parameter.rest.accessToken)) {
						parameter.rest.accessToken = new SessionAccessToken({});
					}

					parameter.rest.accessToken!.expiration = value;
				}
				break;
			case LocalAuthDialogFields.ACCESS_EXPIRATION:
				if (!FunctionHelper.exists(parameter.rest.accessToken)) {
					parameter.rest.accessToken = new SessionAccessToken({});
				}

				parameter.rest.accessToken!.expiration = value;
				break;
			case LocalAuthDialogFields.DISKSPACE_ANONYMOUS:
				if (!FunctionHelper.exists(parameter.rest.limits)) {
					parameter.rest.limits = new SessionLimits({});
				}

				parameter.rest.limits!.diskSpaceLimit = value;
				break;
			case LocalAuthDialogFields.DISKSPACE_USER:
				if (!FunctionHelper.exists(parameter.rest.limits)) {
					parameter.rest.limits = new SessionLimits({});
				}

				parameter.rest.limits!.diskSpaceLimitUser = value;
				break;
			case LocalAuthDialogFields.MAXFILES_ANONYMOUS:
				if (!FunctionHelper.exists(parameter.rest.limits)) {
					parameter.rest.limits = new SessionLimits({});
				}

				parameter.rest.limits!.maxFiles = value;
				break;
			case LocalAuthDialogFields.MAXFILES_USER:
				if (!FunctionHelper.exists(parameter.rest.limits)) {
					parameter.rest.limits = new SessionLimits({});
				}

				parameter.rest.limits!.maxFilesUser = value;
				break;
			case LocalAuthDialogFields.UPLOAD_ANONYMOUS:
				if (!FunctionHelper.exists(parameter.rest.limits)) {
					parameter.rest.limits = new SessionLimits({});
				}

				parameter.rest.limits!.uploadLimit = value;
				break;
			case LocalAuthDialogFields.UPLOAD_USER:
				if (!FunctionHelper.exists(parameter.rest.limits)) {
					parameter.rest.limits = new SessionLimits({});
				}

				parameter.rest.limits!.uploadLimitUser = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let administration: Administration = this.props.context.session.getAdministration();
		let serverConfiguration: Server = (
			await administration.getConfiguration(ConfigurationType.Server) as Server
		).clone();
		serverConfiguration.localAuth = this.state.parameter;

		try {
			await administration.updateConfiguration(ConfigurationType.Server, serverConfiguration);

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.local_auth", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
		this.hide();
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.LOCAL_AUTH;
	}
}

const LocalAuthDialog = React.memo(LocalAuthDialogDefinition,
	(prevProps: Readonly<LocalAuthDialogProperties>, nextProps: Readonly<LocalAuthDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default LocalAuthDialog;